import { useCallback, useEffect, useRef, useState } from "react";
import { authClient } from "@/lib/auth-client";

type AuthUser = {
	id: string;
	email: string;
	name: string;
	image: string | null;
	role?: string | null;
	emailVerified?: boolean;
};

type AuthSession = {
	id: string;
	userId: string;
	expiresAt: Date;
};

export type AuthState = {
	isLoaded: boolean;
	isSignedIn: boolean;
	userId: string | null;
	session: { data: { user: AuthUser; session: AuthSession } | null };
	getToken: () => Promise<string | null>;
	signIn: typeof authClient.signIn;
	signUp: typeof authClient.signUp;
	signOut: () => Promise<void>;
};

let cachedToken: { value: string; expiresAt: number } | null = null;

async function fetchToken(): Promise<string | null> {
	const now = Date.now();
	if (cachedToken && cachedToken.expiresAt - 30_000 > now) {
		return cachedToken.value;
	}
	const res = await fetch("/api/auth/token", {
		method: "GET",
		credentials: "include",
	});
	if (!res.ok) return null;
	const data = (await res.json()) as { token?: string };
	if (!data.token) return null;
	const decoded = decodeJwt(data.token);
	cachedToken = {
		value: data.token,
		expiresAt: decoded?.exp ? decoded.exp * 1000 : now + 5 * 60_000,
	};
	return data.token;
}

function decodeJwt(token: string): { exp?: number; sub?: string } | null {
	try {
		const parts = token.split(".");
		if (parts.length < 2) return null;
		const payload = parts[1] ?? "";
		const padded = payload.replace(/-/g, "+").replace(/_/g, "/");
		const json = atob(padded);
		return JSON.parse(json) as { exp?: number; sub?: string };
	} catch {
		return null;
	}
}

export function useAuth(): AuthState {
	const sessionResp = authClient.useSession();
	const sessionData = (sessionResp as { data?: { user: AuthUser; session: AuthSession } | null }).data ?? null;
	const isPending = Boolean((sessionResp as { isPending?: boolean }).isPending);
	const [, force] = useState(0);
	const signOutInFlight = useRef(false);

	const signOut = useCallback(async () => {
		if (signOutInFlight.current) return;
		signOutInFlight.current = true;
		cachedToken = null;
		try {
			await authClient.signOut();
		} catch {
			/* ignore */
		} finally {
			signOutInFlight.current = false;
		}
		force((n) => n + 1);
	}, []);

	const isLoaded = !isPending;
	const isSignedIn = Boolean(sessionData?.session && sessionData?.user);
	const userId = sessionData?.user?.id ?? null;

	useEffect(() => {
		if (!isSignedIn) cachedToken = null;
	}, [isSignedIn]);

	const getToken = useCallback(async () => {
		if (!isSignedIn) return null;
		return await fetchToken();
	}, [isSignedIn]);

	return {
		isLoaded,
		isSignedIn,
		userId,
		session: { data: sessionData },
		getToken,
		signIn: authClient.signIn,
		signUp: authClient.signUp,
		signOut,
	};
}
